import React, { useState, MouseEvent, useRef } from "react";
import "./Sidebar.scss";

import classNames from "classnames";
import { useSelector } from "react-redux";

import { ReactComponent as LogoIcon } from "src/images/menu-icons/idh-logo.svg";
import { RootState } from "src/redux/reducers";
import { isValueTaskType } from "src/utils/methods";
import { AppConfig } from "../../config/AppConfig";

import { ReactComponent as ArabyAdsIcon } from "./images/araby-ads.svg";
import { ReactComponent as LogotypeIcon } from "./images/indahash-full.svg";
import { getSidebarElement } from "./SidebarItems";

import SidebarItem from "./SidebarItem";
import QuickActions from "./QuickActions/QuickActions";

export default function Sidebar() {
  const [isOpenSidebar, setIsOpenSidebar] = useState<boolean>(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const envName = AppConfig.getEnvironmentName();

  const {
    mainReducer: { identity, activeWorkspaceUuid },
  } = useSelector((state: RootState) => state);

  const baseUrl = `/workspace/${activeWorkspaceUuid}`;

  const sidebarElements = identity?.sidebarItems?.map((item) =>
    getSidebarElement(item),
  );

  const renderLogo = () => {
    if (identity.wsWorkspace?.logo) {
      return (
        <div
          className={classNames("sidebar__brand-logo", {
            "sidebar__brand-logo--hover": isOpenSidebar,
          })}
        >
          <img
            src={
              isOpenSidebar
                ? identity.wsWorkspace.logo
                : identity.wsWorkspace.smallLogo ?? identity.wsWorkspace.logo
            }
            className={
              isOpenSidebar
                ? "sidebar__brand-logo-full"
                : "sidebar__brand-logo-small"
            }
          />
        </div>
      );
    }

    const logoClasses = classNames("sidebar__logo", {
      "sidebar__logo--local": envName === "local" || envName === "localdev",
      "sidebar__logo--dev": envName === "dev",
      "sidebar__logo--beta": envName === "beta",
      "sidebar__logo--stage1": envName === "stage1",
    });

    return (
      <div className={logoClasses}>
        <LogoIcon className="sidebar__logo-shape" />
        <ArabyAdsIcon className="sidebar__logo-araby-ads" />
        <LogotypeIcon className="sidebar__logo-word" />
      </div>
    );
  };

  const handleMouseEnter = (e: MouseEvent<HTMLElement>) => {
    if (e.currentTarget === e.target) {
      setIsOpenSidebar(true);
    }
  };

  const handleMouseLeave = () => {
    setIsOpenSidebar(false);
  };

  return (
    <aside
      ref={sidebarRef}
      className="sidebar"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <nav role="navigation" aria-label="primary">
        {renderLogo()}
        <ul className="sidebar__list">
          {sidebarElements?.map((wsSidebarElement) => {
            if (!wsSidebarElement) return null;

            let path;

            if (isValueTaskType(wsSidebarElement.path)) {
              path = `${baseUrl}/summary`;
            } else {
              path = `${baseUrl}/${wsSidebarElement.path}`;
            }

            if (wsSidebarElement.key === "front_sidebar_quick_actions") {
              return (
                <QuickActions
                  key={wsSidebarElement.key}
                  sidebarRef={sidebarRef}
                />
              );
            }

            return (
              <SidebarItem
                key={wsSidebarElement.key}
                to={path}
                messageId={wsSidebarElement.translateKey}
                defaultMessage={wsSidebarElement.defaultMessage}
                overrideHtml={wsSidebarElement.overrideHtml}
                subscriptionEntitlement={
                  wsSidebarElement.subscriptionEntitlement
                }
              >
                {wsSidebarElement.icon}
              </SidebarItem>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
